import { getAllRecords, getMeta, setMeta } from '../db/indexeddb';
import type { GvmRecord } from '../lib/types';
import { recordRowHtml, escapeHtml, showToast } from './shared';

const MAX_BIG3 = 3;

export async function renderDailyCommand(container: HTMLElement): Promise<void> {
  const records = await getAllRecords(false);
  const moneyGoal = ((await getMeta('money_goal')) as string) || '';
  const big3Ids = ((await getMeta('big3_ids')) as string[]) || [];

  const byId = new Map(records.map((r) => [r.id, r]));
  const big3 = big3Ids.map((id) => byId.get(id)).filter(Boolean) as GvmRecord[];

  const active = records.filter((r) => r.status === 'now').sort((a, b) => b.overall_score - a.overall_score);
  const nextActions = records.filter((r) => r.next_action && !['completed', 'archived'].includes(r.status))
    .sort((a, b) => b.overall_score - a.overall_score);
  const waiting = records.filter((r) => r.status === 'waiting');
  const blocked = records.filter((r) => r.status === 'blocked');
  const inbox = records.filter((r) => r.status === 'inbox');
  const recent = [...records].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 5);

  container.innerHTML = `
    <div class="card" id="money-goal-card">
      <div class="section-title" style="margin:0 0 6px;">Current Money Goal</div>
      <div id="money-goal-display" style="font-size:20px; font-weight:700; ${moneyGoal ? '' : 'color:var(--text-faint); font-weight:400; font-size:14px;'}">
        ${moneyGoal ? escapeHtml(moneyGoal) : 'Tap to set a money goal'}
      </div>
    </div>

    <div class="section-title">
      <span>Today's Big 3</span>
      ${big3.length < MAX_BIG3 ? '<button class="ghost" id="add-big3" style="min-height:32px; padding:0 10px; font-size:12px;">+ Add</button>' : ''}
    </div>
    <div id="big3-list">
      ${big3.length ? big3.map((r) => bigThreeCard(r)).join('') : '<div class="empty">No Big 3 chosen yet today. Pick up to 3 things that matter most.</div>'}
    </div>

    <div class="section-title">Active Priorities <a href="#/map?status=now" style="font-size:11px; color:var(--text-faint); text-decoration:none;">See all →</a></div>
    ${listOrEmpty(active.slice(0, 5), 'Nothing is marked Now.')}

    <div class="section-title">Next Actions <a href="#/map" style="font-size:11px; color:var(--text-faint); text-decoration:none;">See all →</a></div>
    ${listOrEmpty(nextActions.slice(0, 5), 'No next actions set.')}

    <div class="stat-grid" style="margin: 6px 0 4px;">
      <div class="stat-card">
        <div class="label">Waiting</div>
        <div class="value" style="color:var(--status-waiting)">${waiting.length}</div>
      </div>
      <div class="stat-card">
        <div class="label">Blocked</div>
        <div class="value" style="color:var(--status-blocked)">${blocked.length}</div>
      </div>
    </div>

    <div class="section-title">Waiting <a href="#/map?status=waiting" style="font-size:11px; color:var(--text-faint); text-decoration:none;">See all →</a></div>
    ${listOrEmpty(waiting.slice(0, 4), 'Nothing waiting on someone else.')}

    <div class="section-title">Blocked <a href="#/map?status=blocked" style="font-size:11px; color:var(--text-faint); text-decoration:none;">See all →</a></div>
    ${listOrEmpty(blocked.slice(0, 4), 'Nothing blocked right now.')}

    <div class="section-title">Idea Inbox <a href="#/map?status=inbox" style="font-size:11px; color:var(--text-faint); text-decoration:none;">See all →</a></div>
    ${listOrEmpty(inbox.slice(0, 4), 'Inbox is clear.')}

    <div class="section-title">Recently Changed</div>
    ${listOrEmpty(recent, 'No activity yet.')}
  `;

  const goalCard = container.querySelector('#money-goal-card') as HTMLElement;
  goalCard.addEventListener('click', async () => {
    const next = window.prompt('Current money goal (e.g. "$10k MRR by Nov 1"):', moneyGoal);
    if (next === null) return;
    await setMeta('money_goal', next);
    showToast('Money goal updated');
    void renderDailyCommand(container);
  });

  const addBtn = container.querySelector('#add-big3') as HTMLButtonElement | null;
  addBtn?.addEventListener('click', async () => {
    const candidates = records.filter((r) => !big3Ids.includes(r.id) && !['completed', 'archived'].includes(r.status));
    if (!candidates.length) { showToast('Nothing available to add', 'error'); return; }
    const choice = window.prompt(
      'Enter the number of the item to add as a Big 3:\n' +
      candidates.slice(0, 15).map((r, i) => `${i + 1}. ${r.title}`).join('\n'),
    );
    const idx = choice ? parseInt(choice, 10) - 1 : -1;
    if (idx >= 0 && idx < candidates.length) {
      const updated = [...big3Ids, candidates[idx].id].slice(0, MAX_BIG3);
      await setMeta('big3_ids', updated);
      void renderDailyCommand(container);
    }
  });

  container.querySelectorAll('[data-remove-big3]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.removeBig3!;
      const updated = big3Ids.filter((x) => x !== id);
      await setMeta('big3_ids', updated);
      void renderDailyCommand(container);
    });
  });
}

function bigThreeCard(r: GvmRecord): string {
  return `
    <a href="#/record/${encodeURIComponent(r.id)}" class="card tap record-row" style="text-decoration:none; display:flex; border-left: 3px solid var(--accent);">
      <div class="body">
        <div class="title">${escapeHtml(r.title)}</div>
        <div class="meta">${r.next_action ? `→ ${escapeHtml(r.next_action)}` : 'No next action set'}</div>
      </div>
      <button data-remove-big3="${r.id}" class="ghost" style="min-height:32px; padding:0 10px; font-size:11px;">Remove</button>
    </a>
  `;
}

function listOrEmpty(records: GvmRecord[], emptyText: string): string {
  if (!records.length) return `<div class="empty">${escapeHtml(emptyText)}</div>`;
  return records.map(recordRowHtml).join('');
}
